import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { RoleEditor, Tooltip, Badge as VdBadge } from "@vd/ui";
import { Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { admin, brandsQuery, clientVersionsQuery, numbersQuery, qk, usersQuery } from "../../api/admin";
import { errorMessage } from "../../api/client";
import { methodLabel, signinAdmin, signinKeys, type UserSignIn } from "../../api/signin";
import type {
  AdminUser,
  Brand,
  ClientVersion,
  ClientVersions,
  CrmStatus,
  PatchUser,
  PhoneNumber,
  Roles,
} from "../../api/types";
import { isAdmin, managesReps, rolesOf, useCanEdit, useMe } from "../../auth/useMe";
import { DataTable } from "../../components/DataTable";
import {
  InviteBadge,
  InviteEmailNote,
  ResendInviteButton,
  useInvitations,
} from "../../components/InviteStatus";
import { MemberAiConfirm, MemberAiRow, useMemberAi } from "../../components/integrations/ConnectorPanel";
import { SeatField } from "../../components/plans/SeatField";
import { DevicesTab } from "../../components/team/DevicesTab";
import { PeopleChips } from "../../components/team/PeopleChips";
import { firstNames, roleLabels } from "../../components/team/roles";
import { toast } from "../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Segmented,
  Select,
  Switch,
  Tabs,
} from "../../components/ui";
import { fmtPhone, relTime } from "../../lib/format";
import { firstError } from "../../lib/forms";
import { STATUS_BADGE } from "../../lib/labels";
import { useNavigate } from "../../lib/navigate";
import { versionState } from "../../lib/releases";
import { seatLabel } from "../../lib/seats";

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  crm: z.enum(["connected", "needs_reconnect", "disconnected"]).optional().catch(undefined),
  status: z.enum(["active", "invited", "disabled"]).optional().catch(undefined),
  /** A member whose Manage sheet opens with the page (the mode switch from their Team page). */
  member: z.string().optional().catch(undefined),
  /** The Devices tab (admins only). */
  tab: z.enum(["people", "devices"]).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/people")({
  staticData: { crumb: { label: "People", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(usersQuery),
      context.queryClient.prefetchQuery(brandsQuery),
    ]),
  component: UsersPage,
});

const CRM_BADGE: Record<CrmStatus["state"], { label: string; tone: "ok" | "warn" | "neutral" }> = {
  connected: { label: "Connected", tone: "ok" },
  needs_reconnect: { label: "Needs reconnect", tone: "warn" },
  disconnected: { label: "Not connected", tone: "neutral" },
};

const col = createColumnHelper<AdminUser>();

function useUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PatchUser }) => admin.patchUser(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: qk.users });
      const prev = qc.getQueryData<AdminUser[]>(qk.users);
      qc.setQueryData<AdminUser[]>(qk.users, (list) =>
        list?.map((u) => {
          if (u.id !== id) return u;
          const next = { ...u };
          if (patch.roles) next.roles = patch.roles;
          if (patch.manages_all !== undefined) next.manages_all = patch.manages_all;
          if (patch.managers) next.managers = patch.managers;
          if (patch.reps) next.reps = patch.reps;
          if (patch.brand_id !== undefined) next.brand_id = patch.brand_id;
          if (patch.disabled === true) next.status = "disabled";
          if (patch.disabled === false && u.status === "disabled") next.status = "active";
          return next;
        }),
      );
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.users, ctx.prev);
      toast(errorMessage(e), "danger");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

function UsersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const me = useMe();
  const canEdit = useCanEdit();
  const users = useQuery(usersQuery);
  const brands = useQuery(brandsQuery);
  const invites = useInvitations();
  const versions = useQuery(clientVersionsQuery);
  const versionOf = useMemo(() => latestVersions(versions.data), [versions.data]);
  const [inviting, setInviting] = useState(false);
  const [managing, setManaging] = useState<string | null>(search.member ?? null);
  const brandName = useCallback(
    (id: string | null) => brands.data?.find((b) => b.id === id)?.name ?? "",
    [brands.data],
  );
  const nameOf = useCallback(
    (id: string) => users.data?.find((u) => u.id === id)?.name ?? null,
    [users.data],
  );
  // Admins open anyone to edit; a Rep Manager opens their team read only.
  const canOpen = useCallback((_u: AdminUser) => canEdit || managesReps(me), [canEdit, me]);

  // Only admins get each member's sign-in from the server.
  const hasSignIn = (users.data ?? []).some((u) => u.sign_in !== undefined);

  const rows = useMemo(() => {
    const q = search.q?.toLowerCase().trim();
    return (users.data ?? []).filter(
      (u) =>
        (!q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)) &&
        (!search.crm || u.crm.state === search.crm) &&
        (!search.status || u.status === search.status),
    );
  }, [users.data, search]);

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Name",
        cell: (c) => {
          const u = c.row.original;
          return (
            <div className="min-w-0">
              <p className="truncate font-medium">{c.getValue()}</p>
              <p className="truncate text-caption text-ink-2">{u.email}</p>
              <TeamLine user={u} nameOf={nameOf} />
            </div>
          );
        },
      }),
      col.accessor((u) => roleLabels(rolesOf(u)).join(", "), {
        id: "roles",
        header: "Roles",
        cell: (c) => {
          const u = c.row.original;
          return (
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md focus-visible:outline-2 focus-visible:outline-focus"
              aria-label={`${canEdit ? "Edit" : "View"} roles of ${u.name}`}
              onClick={(e) => {
                e.stopPropagation();
                if (canOpen(u)) setManaging(u.id);
              }}
            >
              {roleLabels(rolesOf(u)).map((l) => (
                <Badge key={l}>{l}</Badge>
              ))}
            </button>
          );
        },
      }),
      col.accessor((u) => seatLabel(u.seat, u.marketing_seat), {
        id: "seat",
        header: "Seat",
        cell: (c) =>
          c.row.original.seat || c.row.original.marketing_seat ? (
            <span className="whitespace-nowrap">{c.getValue()}</span>
          ) : (
            <span className="whitespace-nowrap text-ink-3">{c.getValue()}</span>
          ),
      }),
      ...(hasSignIn
        ? [
            col.accessor((u) => methodLabel(u.sign_in?.method) ?? "", {
              id: "sign_in",
              header: "Sign-in",
              cell: (c) => <SignInCell s={c.row.original.sign_in} />,
            }),
          ]
        : []),
      col.accessor((u) => brandName(u.brand_id), {
        id: "brand",
        header: "Brand",
        cell: (c) => c.getValue() || <span className="text-ink-3">All</span>,
      }),
      col.accessor("status", {
        header: "Status",
        cell: (c) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone={STATUS_BADGE[c.getValue()].tone}>{STATUS_BADGE[c.getValue()].label}</Badge>
            {c.getValue() === "invited" ? (
              <InviteBadge invitation={invites.byUser.get(c.row.original.id)} />
            ) : null}
          </span>
        ),
      }),
      col.accessor((u) => u.numbers.join(", "), {
        id: "numbers",
        header: "Numbers",
        enableSorting: false,
        cell: (c) =>
          c.row.original.numbers.length ? (
            <span className="tnum">{c.row.original.numbers.map(fmtPhone).join(", ")}</span>
          ) : (
            <span className="text-ink-3">None</span>
          ),
      }),
      col.accessor((u) => u.crm.state, {
        id: "crm",
        header: "CRM",
        cell: (c) => {
          const b = CRM_BADGE[c.row.original.crm.state];
          return (
            <Badge tone={b.tone}>
              {c.row.original.crm.kind === "odoo" && b.tone !== "neutral" ? `Odoo: ${b.label}` : b.label}
            </Badge>
          );
        },
      }),
      col.accessor((u) => versionOf.get(u.id)?.app_version ?? "", {
        id: "app_version",
        header: "App version",
        cell: (c) => {
          const v = versionOf.get(c.row.original.id);
          if (!v?.app_version) return <span className="text-ink-3">None</span>;
          const behind =
            versions.data && versionState(v.app_version, v.os, versions.data.latest_stable) === "behind";
          return (
            <span className={behind ? "tnum whitespace-nowrap text-ink-3" : "tnum whitespace-nowrap"}>
              {v.app_version}
            </span>
          );
        },
      }),
      col.accessor("last_seen_at", {
        header: "Last seen",
        sortingFn: (a, b) => (a.original.last_seen_at ?? "").localeCompare(b.original.last_seen_at ?? ""),
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{relTime(c.getValue())}</span>,
      }),
      col.accessor("active_sessions", {
        header: "Sessions",
        meta: { align: "right" },
      }),
      ...(canEdit
        ? [
            col.display({
              id: "actions",
              header: () => <span className="sr-only">Actions</span>,
              cell: (c) => (
                <span className="flex items-center justify-end gap-1">
                  {c.row.original.status === "invited" ? (
                    <ResendInviteButton
                      invitation={invites.byUser.get(c.row.original.id)}
                      userId={c.row.original.id}
                      name={c.row.original.email}
                    />
                  ) : null}
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setManaging(c.row.original.id);
                    }}
                  >
                    Manage
                  </Button>
                </span>
              ),
            }),
          ]
        : []),
    ],
    [canEdit, canOpen, brandName, invites.byUser, nameOf, hasSignIn, versionOf, versions.data],
  );

  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (p) => ({ ...p, ...patch }), replace: true });
  const managed = users.data?.find((u) => u.id === managing) ?? null;
  // Admins get the company's devices on a second tab; managers never see it.
  const tab = isAdmin(me) && search.tab === "devices" ? "devices" : "people";

  return (
    <>
      <PageHeader
        title="People"
        {...(isAdmin(me) ? {} : { children: "Your team" })}
        actions={
          canEdit && tab === "people" ? (
            <Button variant="primary" onClick={() => setInviting(true)}>
              Invite user
            </Button>
          ) : undefined
        }
      />
      {isAdmin(me) ? (
        <Tabs
          label="People"
          value={tab}
          onChange={(t) => set({ tab: t === "people" ? undefined : t })}
          tabs={[
            { value: "people", label: "People" },
            { value: "devices", label: "Devices" },
          ]}
        />
      ) : null}
      {tab === "devices" ? <DevicesTab /> : null}
      {canEdit && tab === "people" ? <InviteEmailNote configured={invites.emailConfigured} /> : null}
      {tab === "people" ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative w-64">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-2" />
              <Input
                aria-label="Search people"
                placeholder="Name or email"
                className="pl-8"
                value={search.q ?? ""}
                onChange={(e) => set({ q: e.target.value || undefined })}
              />
            </div>
            <Select
              aria-label="Status"
              className="w-36"
              value={search.status ?? ""}
              onValueChange={(v) => set({ status: (v || undefined) as never })}
              options={[
                { value: "", label: "Any status" },
                { value: "active", label: "Active" },
                { value: "invited", label: "Invited" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
            <Select
              aria-label="CRM connection"
              className="w-44"
              value={search.crm ?? ""}
              onValueChange={(v) => set({ crm: (v || undefined) as never })}
              options={[
                { value: "", label: "Any CRM state" },
                { value: "connected", label: "Connected" },
                { value: "needs_reconnect", label: "Needs reconnect" },
                { value: "disconnected", label: "Not connected" },
              ]}
            />
          </div>
          <Card>
            {users.isPending ? (
              <Loading />
            ) : users.isError ? (
              <ErrorState error={users.error} onRetry={() => users.refetch()} />
            ) : (
              <DataTable
                data={rows}
                columns={columns}
                getRowId={(u) => u.id}
                initialSorting={[{ id: "name", desc: false }]}
                onRowClick={(u: AdminUser) => {
                  if (canOpen(u)) setManaging(u.id);
                }}
                rowLabel={(u: AdminUser) => `Manage ${u.name}`}
                rowClassName={(u) => (u.status === "disabled" ? "opacity-60" : undefined)}
                empty={<EmptyState title="No one matches" />}
              />
            )}
          </Card>
        </>
      ) : null}
      {canEdit ? (
        <InviteDialog
          open={inviting}
          onClose={() => setInviting(false)}
          brands={brands.data ?? []}
          users={users.data ?? []}
        />
      ) : null}
      {managed && canOpen(managed) ? (
        <ManageDialog
          user={managed}
          brands={brands.data ?? []}
          users={users.data ?? []}
          onClose={() => setManaging(null)}
        />
      ) : null}
    </>
  );
}

/** Each member's most recently seen desktop app. */
function latestVersions(d: ClientVersions | undefined): Map<string, ClientVersion> {
  const out = new Map<string, ClientVersion>();
  for (const c of d?.items ?? []) {
    const had = out.get(c.user_id);
    if (!had || (c.seen_at ?? "") > (had.seen_at ?? "")) out.set(c.user_id, c);
  }
  return out;
}

/** Names for a caption, with people this viewer can't see counted: "Chris and 1 more". */
function namesLine(ids: string[], nameOf: (id: string) => string | null): string {
  const known = ids.map(nameOf).filter((n): n is string => n !== null);
  const more = ids.length - known.length;
  const names = firstNames(known);
  if (!more) return names;
  return names ? `${names} and ${more} more` : `${more} ${more === 1 ? "person" : "people"}`;
}

/** "Managed by Carla, Dan" for a rep; "Manages all reps" or "Manages Jared, Ann" for a manager. */
function TeamLine({ user, nameOf }: { user: AdminUser; nameOf: (id: string) => string | null }) {
  const r = rolesOf(user);
  const lines: string[] = [];
  if (r.rep && user.managers?.length) lines.push(`Managed by ${namesLine(user.managers, nameOf)}`);
  if (r.manager) {
    if (user.manages_all) lines.push("Manages all reps");
    else if (user.reps?.length) lines.push(`Manages ${namesLine(user.reps, nameOf)}`);
  }
  if (!lines.length) return null;
  return <p className="truncate text-caption text-ink-3">{lines.join(". ")}</p>;
}

const NEW_ROLES: Roles = { rep: true, support: false, manager: false, admin: false };

/** Members who can be picked as managers, or as reps, besides `except`. */
function holders(users: AdminUser[], key: keyof Roles, except?: string) {
  return users
    .filter((u) => u.id !== except && u.status !== "disabled" && rolesOf(u)[key])
    .map((u) => ({ id: u.id, name: u.name || u.email }));
}

const inviteSchema = z.object({
  email: z.string().trim().min(1, "Enter an email address.").email("Enter a valid email address."),
  brand_id: z.string(),
});

function InviteDialog({
  open,
  onClose,
  brands,
  users,
}: {
  open: boolean;
  onClose: () => void;
  brands: Brand[];
  users: AdminUser[];
}) {
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Roles>(NEW_ROLES);
  const [managers, setManagers] = useState<string[]>([]);
  const form = useForm({
    defaultValues: {
      email: "",
      brand_id: brands[0]?.id ?? "",
    },
    validators: { onSubmit: inviteSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      try {
        const u = await admin.inviteUser({
          email: value.email.trim(),
          roles,
          brand_id: value.brand_id || null,
          managers: roles.rep || roles.support ? managers : [],
        });
        qc.setQueryData<AdminUser[]>(qk.users, (list) => (list ? [...list, u] : [u]));
        void qc.invalidateQueries({ queryKey: qk.invitations });
        toast(`${u.email} is invited.`);
        form.reset();
        setRoles(NEW_ROLES);
        setManagers([]);
        onClose();
      } catch (e) {
        setServerError(errorMessage(e));
      }
    },
  });
  const close = () => {
    form.reset();
    setRoles(NEW_ROLES);
    setManagers([]);
    setServerError(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} title="Invite user">
      <form
        id="invite-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <form.Field name="email">
          {(f) => (
            <Field label="Email" error={firstError(f.state.meta.errors)}>
              <Input
                id="invite-email"
                type="email"
                autoComplete="off"
                autoFocus
                value={f.state.value}
                onBlur={f.handleBlur}
                onChange={(e) => f.handleChange(e.target.value)}
                aria-invalid={f.state.meta.errors.length > 0 || undefined}
              />
            </Field>
          )}
        </form.Field>
        <RoleEditor value={roles} onChange={setRoles} />
        {roles.rep || roles.support ? (
          <Field label="Managers">
            <PeopleChips
              label="Managers"
              chosen={managers}
              options={holders(users, "manager")}
              onChange={setManagers}
              empty="No manager"
              addLabel="Add a manager"
            />
          </Field>
        ) : null}
        <form.Field name="brand_id">
          {(f) => (
            <Field label="Brand">
              <Select
                id="invite-brand"
                value={f.state.value}
                onValueChange={(v) => f.handleChange(v)}
                options={[
                  { value: "", label: "All brands" },
                  ...(brands ?? []).map((b) => ({ value: b.id, label: b.name })),
                ]}
              />
            </Field>
          )}
        </form.Field>
        {serverError ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-ui text-danger-ink">
            {serverError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={close}>Cancel</Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button type="submit" variant="primary" busy={submitting}>
                Send invite
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </Dialog>
  );
}

function ManageDialog({
  user,
  brands,
  users,
  onClose,
}: {
  user: AdminUser;
  brands: Brand[];
  users: AdminUser[];
  onClose: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const patch = useUserMutation();
  const admin_ = isAdmin(me);
  const numbers = useQuery({ ...numbersQuery, enabled: admin_ });
  const [confirm, setConfirm] = useState<"disable" | "revoke" | null>(null);
  const isSelf = user.id === me.user.id;
  // The company's last active admin keeps Admin (the server refuses it too).
  const lastAdmin =
    user.roles.admin && !users.some((u) => u.id !== user.id && u.status === "active" && u.roles.admin);
  const roles = rolesOf(user);
  const save = (p: PatchUser, done: string) =>
    patch.mutate({ id: user.id, patch: p }, { onSuccess: () => toast(done) });
  const memberAi = useMemberAi();

  const revoke = useMutation({
    mutationFn: () => admin.revokeUser(user.id),
    onSuccess: () => {
      qc.setQueryData<AdminUser[]>(qk.users, (l) =>
        l?.map((u) => (u.id === user.id ? { ...u, active_sessions: 0 } : u)),
      );
      toast(`${user.name} is signed out everywhere.`);
      setConfirm(null);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const assign = useMutation({
    mutationFn: ({ n, owner }: { n: PhoneNumber; owner: string | null }) =>
      admin.patchNumber(n.id, { owner_user_id: owner }),
    onMutate: async ({ n, owner }) => {
      await qc.cancelQueries({ queryKey: qk.numbers });
      const prev = qc.getQueryData<PhoneNumber[]>(qk.numbers);
      qc.setQueryData<PhoneNumber[]>(qk.numbers, (l) =>
        l?.map((x) => (x.id === n.id ? { ...x, owner_user_id: owner } : x)),
      );
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.numbers, ctx.prev);
      toast(errorMessage(e), "danger");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.numbers });
      qc.invalidateQueries({ queryKey: qk.users });
    },
  });

  const owned = (numbers.data ?? []).filter((n) => n.owner_user_id === user.id);
  const available = (numbers.data ?? []).filter(
    (n) => n.owner_user_id !== user.id && (!user.brand_id || n.brand_id === user.brand_id),
  );
  const [toAdd, setToAdd] = useState("");

  if (!admin_) {
    // A Rep Manager sees their rep; everything else is the admins'.
    return (
      <Dialog open onClose={onClose} title={user.name}>
        <div className="flex flex-col gap-4">
          <p className="-mt-1 text-ui text-ink-2">{user.email}</p>
          <p className="flex flex-wrap gap-1">
            {roleLabels(roles).map((l) => (
              <Badge key={l}>{l}</Badge>
            ))}
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={confirm === null && !memberAi.asking} onClose={onClose} title={user.name}>
        <div className="flex flex-col gap-5">
          <p className="-mt-1 text-ui text-ink-2">{user.email}</p>
          <RoleEditor
            value={roles}
            self={isSelf}
            lastAdmin={lastAdmin}
            breakGlass={user.sign_in?.break_glass === true}
            name={user.name}
            onChange={(r) => save({ roles: r }, "Roles updated.")}
          />
          <SeatField user={user} roles={roles} />
          {roles.rep || roles.support ? (
            <Field label="Managers">
              <PeopleChips
                label="Managers"
                chosen={user.managers ?? []}
                options={holders(users, "manager", user.id)}
                onChange={(ids) => save({ managers: ids }, "Managers updated.")}
                empty="No manager"
                addLabel="Add a manager"
              />
            </Field>
          ) : null}
          {roles.manager ? (
            <Field label="Manages">
              <div className="flex flex-col gap-2">
                <Segmented
                  label="Manages"
                  value={user.manages_all ? "all" : "chosen"}
                  onChange={(v) =>
                    save(
                      { manages_all: v === "all" },
                      v === "all" ? "Manages all reps." : "Manages chosen people.",
                    )
                  }
                  options={[
                    { value: "all", label: "All reps" },
                    { value: "chosen", label: "Chosen people" },
                  ]}
                />
                {user.manages_all ? null : (
                  <PeopleChips
                    label="Reps"
                    chosen={user.reps ?? []}
                    options={holders(users, "rep", user.id)}
                    onChange={(ids) => save({ reps: ids }, "Reps updated.")}
                    empty="No reps yet"
                    addLabel="Add a rep"
                  />
                )}
              </div>
            </Field>
          ) : null}
          <Field label="Brand">
            <Select
              id="m-brand"
              value={user.brand_id ?? ""}
              onValueChange={(v) => save({ brand_id: v || null }, "Brand updated.")}
              options={[
                { value: "", label: "All brands" },
                ...(brands ?? []).map((b) => ({ value: b.id, label: b.name })),
              ]}
            />
          </Field>
          <MemberAiRow userId={user.id} ai={memberAi} />
          {user.sign_in ? <SignInGroup user={user} s={user.sign_in} /> : null}

          {roles.rep || owned.length > 0 ? (
            <section>
              <h3 className="mb-2 text-ui font-medium">Numbers</h3>
              {owned.length === 0 ? <p className="text-ui text-ink-2">No numbers assigned.</p> : null}
              {!roles.rep ? (
                <p className="text-caption text-ink-3">
                  Numbers are for reps. Unassign these, or give {user.name} the Sales rep role.
                </p>
              ) : null}
              <ul className="flex flex-col gap-1.5">
                {owned.map((n) => (
                  <li
                    key={n.id}
                    className="flex items-center gap-3 rounded-lg border border-hairline px-3 py-1.5 text-ui"
                  >
                    <span className="tnum font-medium">{fmtPhone(n.e164)}</span>
                    <span className="text-ink-2">{n.label}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => assign.mutate({ n, owner: null })}
                    >
                      Unassign
                    </Button>
                  </li>
                ))}
              </ul>
              {roles.rep && available.length ? (
                <div className="mt-2 flex gap-2">
                  <Select
                    aria-label="Number to assign"
                    className="flex-1"
                    value={toAdd}
                    onValueChange={(v) => setToAdd(v)}
                    options={[
                      { value: "", label: "Choose a number" },
                      ...(available ?? []).map((n) => ({
                        value: n.id,
                        label: `${fmtPhone(n.e164)} ${n.label ? `(${n.label})` : ""}${" "}
                      ${n.owner_user_id ? ", reassigns from current owner" : ""}`,
                      })),
                    ]}
                  />
                  <Button
                    disabled={!toAdd}
                    onClick={() => {
                      const n = available.find((x) => x.id === toAdd);
                      if (n)
                        assign.mutate(
                          { n, owner: user.id },
                          {
                            onSuccess: () => toast(`${fmtPhone(n.e164)} assigned to ${user.name}.`),
                          },
                        );
                      setToAdd("");
                    }}
                  >
                    Assign
                  </Button>
                </div>
              ) : null}
            </section>
          ) : null}

          <section>
            <h3 className="mb-2 text-ui font-medium">CRM</h3>
            <p className="text-ui">
              <Badge tone={CRM_BADGE[user.crm.state].tone}>{CRM_BADGE[user.crm.state].label}</Badge>
              {user.crm.identity ? (
                <span className="ml-2 text-ink-2">
                  as {user.crm.identity.name} on {user.crm.identity.web_base_url}
                </span>
              ) : null}
            </p>
            {user.crm.state === "needs_reconnect" ? (
              <p className="mt-1.5 text-ui text-ink-2">
                {user.name.split(" ")[0]} reconnects from the desktop app under Integrations.
              </p>
            ) : null}
          </section>

          <section className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
            <Button onClick={() => setConfirm("revoke")} disabled={user.active_sessions === 0}>
              Sign out everywhere
            </Button>
            <span className="text-caption text-ink-2">
              {user.active_sessions} active {user.active_sessions === 1 ? "session" : "sessions"}
            </span>
            {user.status === "disabled" ? (
              <Button
                className="ml-auto"
                onClick={() =>
                  patch.mutate(
                    { id: user.id, patch: { disabled: false } },
                    { onSuccess: () => toast(`${user.name} is enabled.`) },
                  )
                }
              >
                Enable user
              </Button>
            ) : (
              <Button
                className="ml-auto"
                variant="danger"
                disabled={isSelf}
                onClick={() => setConfirm("disable")}
              >
                Disable user
              </Button>
            )}
          </section>
        </div>
      </Dialog>

      <MemberAiConfirm ai={memberAi} />
      <Dialog
        open={confirm === "disable"}
        onClose={() => setConfirm(null)}
        title={`Disable ${user.name}?`}
        tone="danger"
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Cancel</Button>
            <Button
              variant="danger"
              busy={patch.isPending}
              onClick={() =>
                patch.mutate(
                  { id: user.id, patch: { disabled: true } },
                  {
                    onSuccess: () => {
                      toast(`${user.name} is disabled.`);
                      setConfirm(null);
                    },
                  },
                )
              }
            >
              Disable
            </Button>
          </>
        }
      >
        <p className="text-ui">
          They're signed out of every device right away and their calling credential stops working. Their
          calls and notes stay.
        </p>
      </Dialog>

      <Dialog
        open={confirm === "revoke"}
        onClose={() => setConfirm(null)}
        title={`Sign ${user.name} out everywhere?`}
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="primary" busy={revoke.isPending} onClick={() => revoke.mutate()}>
              Sign out
            </Button>
          </>
        }
      >
        <p className="text-ui">They can sign in again.</p>
      </Dialog>
    </>
  );
}

/** The Users column: the newest session's method, and Exception or Break-glass. */
function SignInCell({ s }: { s: UserSignIn | undefined }) {
  if (!s) return null;
  const label = methodLabel(s.method);
  return (
    <span className="flex flex-wrap items-center gap-1">
      {label ? <Badge>{label}</Badge> : <span className="text-ink-3">Not yet</span>}
      {s.password_exception ? <VdBadge tone="outline">Exception</VdBadge> : null}
      {s.break_glass ? <VdBadge tone="outline">Break-glass</VdBadge> : null}
    </span>
  );
}

/** "Password: Set · 2 passkeys", or "No password". */
function credentialLine(s: UserSignIn): string {
  const keys = s.passkeys === 1 ? "1 passkey" : `${s.passkeys} passkeys`;
  if (!s.password_set) return s.passkeys ? `No password · ${keys}` : "No password";
  return `Password: Set · ${keys}`;
}

/** The Manage dialog's Sign-in group (admins). */
function SignInGroup({ user, s }: { user: AdminUser; s: UserSignIn }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState(s.exception_reason ?? "");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.users });
    void qc.invalidateQueries({ queryKey: signinKeys.settings });
  };
  const put = useMutation({
    mutationFn: (body: Parameters<typeof signinAdmin.putUser>[1]) => signinAdmin.putUser(user.id, body),
    onSuccess: (next) => {
      qc.setQueryData<AdminUser[]>(qk.users, (l) =>
        l?.map((u) => (u.id === user.id ? { ...u, sign_in: next } : u)),
      );
      toast("Saved");
      refresh();
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const link = useMutation({
    mutationFn: () => signinAdmin.passwordLink(user.id),
    onSuccess: () => toast("Link sent"),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const admin_ = user.roles.admin;
  const breakGlass = (
    <Switch
      label="Break-glass"
      checked={s.break_glass}
      disabled={!admin_ || put.isPending}
      onChange={(v) => put.mutate({ break_glass: v })}
    />
  );
  return (
    <section aria-label="Sign-in">
      <h3 className="mb-2 font-medium text-ui">Sign-in</h3>
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-ui">Allow password</span>
          <Switch
            label="Allow password"
            checked={s.password_exception}
            disabled={put.isPending}
            onChange={(v) => put.mutate({ password_exception: v, exception_reason: reason.trim() })}
          />
        </div>
        {s.password_exception ? (
          <Field label="Reason">
            <Input
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => {
                if (reason.trim() !== (s.exception_reason ?? ""))
                  put.mutate({ password_exception: true, exception_reason: reason.trim() });
              }}
            />
          </Field>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <span className="text-ui">Break-glass</span>
          {admin_ ? (
            breakGlass
          ) : (
            <Tooltip content="Only admins can be break-glass admins">
              <span>{breakGlass}</span>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-caption text-ink-3">{credentialLine(s)}</span>
          <Button size="sm" busy={link.isPending} onClick={() => link.mutate()}>
            Send password link
          </Button>
        </div>
      </div>
    </section>
  );
}
